import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TipScreen } from '@/features/tip/TipScreen';
import { LoadingScreen } from '@/features/check/StatusScreen';
import { availablePaymentMethods, requiresMethodSelection } from '@/features/payment/availability';
import type { WalletCapabilities } from '@/features/payment/availability';
import {
    resolveWalletCapabilities,
    useWalletCapabilities,
} from '@/features/payment/wallets/useWalletCapabilities';
import { startApplePaySheetFor } from '@/features/payment/wallets/applePaySheet';
import { isCardOnly } from '@/payment/cardOnly';
import { loadDraft, saveDraft } from '@/payment/draft';
import { useCheckout } from './checkoutContext';

/**
 * The tip step, and the point where the flow decides whether the guest is asked how to pay.
 *
 * The skip lives here rather than in the payment screen because a screen cannot decline to exist. If card is
 * the only way this guest can pay - no wallets enabled, or the only one enabled cannot run on this phone -
 * the selection step is not rendered and then hidden, it is never routed to.
 */
export function TipRoute() {
    const { location, check } = useCheckout();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    /*
     * Started here rather than at the moment it is needed. Google's half of the answer costs an SDK fetch and
     * a round trip into the guest's Google account, so asking while they are still choosing a tip means the
     * answer is already in hand when they tap Pay.
     *
     * READ DURING RENDER as well as awaited below, because Apple Pay cannot be opened after an await. Safari
     * refuses to construct a session outside the synchronous part of a gesture handler, so when this has
     * already resolved - which it has, in every case but a guest faster than their own network - the tap
     * decides from `capabilities.data` and never yields.
     */
    const capabilities = useWalletCapabilities(location);

    // Read once per check. Restores a tip the guest already chose if they came back to this step - and
    // carries forward a loyalty redemption made before it, which writing a fresh draft here would erase.
    const draft = useMemo(() => loadDraft(check.id), [check.id]);

    /**
     * @param replace
     * Leave this step out of history rather than behind the guest. Used when the step was skipped: a back
     * gesture must not land on a screen that immediately forwards, which is a back button that does nothing.
     */
    const proceed = (capabilities: WalletCapabilities, tipCents: number, replace: boolean) => {
        const methods = availablePaymentMethods(location, capabilities, isCardOnly());

        if (requiresMethodSelection(methods)) {
            saveDraft({ ...draft, checkId: check.id, tipCents });
            navigate('/payment', { replace });
            return;
        }

        // Only one way to pay, so record it exactly as if the guest had picked it. Everything downstream then
        // reads the same draft whether or not the selection step happened.
        const method = methods[0];

        // Before the navigation and before anything is awaited, because that is the only place Safari will
        // build one. See `startApplePaySheetFor`.
        if (method === 'applePay') startApplePaySheetFor(location, check.amountDueCents + tipCents);

        saveDraft({ ...draft, checkId: check.id, tipCents, method });
        navigate('/pay', { replace });
    };

    /*
     * SYNCHRONOUS WHENEVER IT CAN BE, which is the whole shape of this function.
     *
     * Awaiting the probe here is what stopped Apple Pay ever opening: Safari refuses to construct a session
     * outside the synchronous part of a gesture handler, and an await ends that. The probe was started when
     * this step mounted and has answered long before a guest has finished choosing a tip, so the normal path
     * reads it from render state and never yields.
     *
     * The await remains for the case where it genuinely has not answered - a guest faster than their own
     * network. Apple Pay cannot open on that path, and says so rather than failing silently.
     */
    const handlePay = (tipCents: number, replace = false) => {
        const known = capabilities.data;

        if (known) {
            proceed(known, tipCents, replace);
            return;
        }

        void resolveWalletCapabilities(queryClient, location).then((resolved) =>
            proceed(resolved, tipCents, replace),
        );
    };

    /*
     * A location that does not take tips never sees this screen.
     *
     * Same reasoning as the payment-method skip above: a screen cannot decline to exist. With tipping off
     * there is nothing on this one but a total and a button, and the custom-amount field - which is this
     * app's own control - would still be sitting there inviting an amount the restaurant will not take.
     *
     * handlePay(0) rather than a plain redirect, so the same draft is written and the same choice about the
     * payment-method step is made. Skipping a step must not mean skipping what it decided.
     */
    const skipped = useRef(false);
    useEffect(() => {
        if (check.tipsEnabled || skipped.current) return;

        skipped.current = true;

        // replace, so this step is not left in history. The guest goes check -> payment and back again, with
        // nothing in between that they can land on - a browser back gesture is the common one on a phone, and
        // it does not go through the in-app back button.
        void handlePay(0, true);
    });

    if (!check.tipsEnabled) return <LoadingScreen />;

    return (
        <TipScreen
            location={location}
            check={check}
            initialTipCents={draft?.tipCents}
            onPay={handlePay}
            onBack={() => navigate('/check')}
        />
    );
}
